import { Component, createRef } from 'react';

import { debounceTime, filter } from 'rxjs/operators';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { PageDataProviderService } from '../../services/page-data-provider.service';
import { SettingsService } from '../../services/settings.service';
import { SpinnerService } from '../../services/spinner.service';
import { UIContext } from '../../services/ui-context';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { ConvertFromVideo } from '../../types/capability.types';
import { FileSelectEvent } from '../../types/file-upload.types';
import { PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { FromTypes, ProcessStageSubStateTypes, ProcessStageTypes, ProcessTypes, ToTypes } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { getFileMimeTypeFromFilename } from '../../utils/file';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { cloneDeep, get, includes, isArray } from '../../utils/lodash-min';
import { ProcessorMetadataUtils } from '../../utils/processor-metadata.utils';
import { MotiveConfig } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import { FileUploadManager, SupportedFileTypes } from '../converter/file-upload-manager';
import { AV_JOB_TREE } from './av-job-options';

export const PLAYABLE_FILE_MIMI_TYPES = [
    'video/mp4', 'video/webm', 'audio/webm',
    'audio/mpeg', 'audio/MPA', 'audio/mpa-robust',
    'audio/vnd.wave', 'audio/wav', 'audio/wave', 'audio/x-wa'
];

export interface AVBaseState {
    fileSelected: boolean;
    fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[];
    fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[];
    isPlaying: boolean;
    isMuted: boolean;
    isLoading: boolean;
}

/**
 * The trimmer / cutter output dropdown: a curated subset of xconfig's trim outputs (every entry is an output of the
 * video-trimmer / audio-trimmer tree; av-job-options.xconfig.drift.spec.ts pins that). M4R (iPhone ringtone) has been an
 * xconfig audio output since 2026-10-11, so its job carries xconfig's AAC like every other entry.
 */
export const AV_OUTPUT_TYPES = {
    video: [
        { id: 'same', text: 'Same As Input', item: 'same', selected: true },
        { id: 'mp4', text: 'MP4', item: '.mp4', selected: false },
        { id: 'mkv', text: 'MKV', item: '.mkv', selected: false },
        { id: 'mov', text: 'MOV', item: '.mov', selected: false },
        { id: 'webm', text: 'WebM', item: '.webm', selected: false },
        { id: 'mts', text: 'MTS', item: '.mts', selected: false },
        { id: 'avi', text: 'AVI', item: '.avi', selected: false },
    ],
    audio: [
        { id: 'same', text: 'Same As Input (Audio only)', item: 'same', selected: true },
        { id: 'mp3', text: 'MP3', item: '.mp3', selected: false },
        { id: 'flac', text: 'FLAC', item: '.flac', selected: false },
        { id: 'wav', text: 'WAV', item: '.wav', selected: false },
        { id: 'm4a', text: 'M4A', item: '.m4a', selected: false },
        { id: 'opus', text: 'OPUS', item: '.opus', selected: false },
        { id: 'm4r', text: 'M4R', item: '.m4r', selected: false },
    ],
} as const;

/**
 * xconfig option trees for trim / cut jobs (components/shared/av-job-options.ts), one fetch per tree per page life.
 * A failed fetch is forgotten, so the next click asks again.
 */
const avJobTrees = new Map<string, Promise<unknown>>();

export abstract class AVBaseComponent<S extends AVBaseState> extends Component<RouteProps, S> {
    protected abstract readonly logger: Logger;

    get environment() {
        return this.envService.env;
    }
    get domainname() {
        return this.environment.domainname;
    }
    get sitename() {
        return this.environment.sitename;
    }
    get showRating() {
        return this.environment.sitename === this.environment.constants.xconvertId;
    }
    get showInfoGrid() {
        return this.environment.sitename === this.environment.constants.xconvertId;
    }
    get internalAdsEnabled() {
        return this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
    }
    get leftAdShown() {
        return this.effectiveMotives && this.effectiveMotives.convertPageLeft && !this.internalAdsEnabled;
    }
    get rightAdShown() {
        return this.effectiveMotives && this.effectiveMotives.convertPageRight && !this.internalAdsEnabled;
    }
    get shouldShowLoading() {
        return this.state.fakeUploadInProgressFileForSelectedFiles.length;
    }
    get getLoadingFileList() {
        return Array.from(this.state.fakeUploadInProgressFileForSelectedFiles);
    }
    get outputVideoElement(): HTMLVideoElement {
        return this.outputVideoRef && this.outputVideoRef.current;
    }
    get outputVideoCurrentTime(): number {
        return this.outputVideoRef && this.outputVideoRef.current && this.outputVideoRef.current.currentTime || 0;
    }

    public title: string;
    public description: string;
    public features: Array<SeoFeature>;
    public pageData: PageData;
    public showHTML = false;
    public isMobile: boolean;
    public sourceFileUploadArray: FileUpload[] = [];
    public feature;
    public markdownData;

    featureDescriptionsDisabled = false;
    sidePanelFileUploads: FileUpload[];
    showSidePanelTriggered = false;
    canShowLivePreview = false;
    showLivePreview = false;
    effectiveMotives: MotiveConfig;
    initialized = false;
    processType = 'convert';
    fileUrl: string;
    fileObjectURL: string;
    videoDuration: number;
    selectedToType = 'same';
    supportedAVTypes: any[] = [];

    outputVideoRef = createRef<HTMLVideoElement>();
    savedFilesRef = createRef<HTMLDivElement>();

    @lazyInject(WebSocketService) protected websocketService: WebSocketService;
    @lazyInject(UIContext) protected uiContext: UIContext;
    @lazyInject(SpinnerService) protected spinnerService: SpinnerService;
    @lazyInject(FileProcessService) protected fileProcessService: FileProcessService;
    @lazyInject(UploadedFilesService) protected uploadedFilesService: UploadedFilesService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(MotivesService) protected motivesService: MotivesService;
    @lazyInject(SettingsService) protected settingsService: SettingsService;
    @lazyInject(EnvironmentService) protected envService: EnvironmentService;
    @lazyInject(FileUploadService) protected fileUploadService: FileUploadService;
    @lazyInject(FileManagerService) protected fileManagerService: FileManagerService;
    @lazyInject(LightHttp) protected lightHttp: LightHttp;
    @lazyInject(PageDataProviderService) protected pageDataProvider: PageDataProviderService;

    constructor(props) {
        super(props);
        this.loadComponentData(this.props);
        this.updatePageData(props.pageData);
        this.updateSupportedAVTypes(props.pageData);
    }

    loadComponentData(props) {
        this.feature = RouteUtils.getPathFromProps(props);
        this.isMobile = BrowserUtils.isMobile();
    }

    componentDidMount() {
        this.fileManagerService.fileUploadChanged.pipe(
            untilDestroyed(this),
            debounceTime(100)
        ).subscribe({
            next: (fileUpload) => {
                if (fileUpload.result) {
                    const websocketResponse = fileUpload.ws;
                    if (websocketResponse && websocketResponse.processStage === ProcessStageTypes.COMPLETED && ProcessStageSubStateTypes.COMPLETED) {
                        this.updateVideoSource(fileUpload);
                    }
                }
                this.forceUpdate();
            }
        });

        const componentMountedTime = Date.now();
        this.fileUploadService.onFileSelected.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelected) {
                    this.setState({ fileSelected: true } as Partial<S> as any);
                }
            }
        });
        this.isMobile = BrowserUtils.isMobile();
        this.feature = RouteUtils.getPathFromProps(this.props);

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe(
                (motives) => this.effectiveMotives = motives,
                (error) => console.error(error),
            );
        this.settingsService.userSettings
            .pipe(untilDestroyed(this))
            .subscribe(
                (userSettings) => {
                    this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
                },
            );
        this.settingsService.featureFlags
            .pipe(untilDestroyed(this))
            .subscribe(
                (ff) => {
                    this.showLivePreview = get(ff, 'showLivePreview', false);
                },
            );
    }

    componentWillUnmount() {
        // for untilDestroyed
    }

    toggleMute() {
        this.outputVideoElement.muted = !this.outputVideoElement.muted;
        this.setState({ isMuted: this.outputVideoElement.muted } as Partial<S> as any);
    }

    handleScrub(event: any) {
        const time = parseFloat(event.target.value);
        this.outputVideoElement.currentTime = time;
        this.forceUpdate();
    }

    async fileSelected(event: FileSelectEvent) {
        this.setState({ isLoading: true } as Partial<S> as any);

        try {
            this.fileUrl = null;
            const URL = window.URL || window.webkitURL;
            if (this.fileObjectURL) {
                URL.revokeObjectURL(this.fileObjectURL);
            }
            this.fileObjectURL = null;

            const isAudio = this.pageData.fromType.indexOf(FromTypes.AUDIO) > -1;
            const fileUploadManager = new FileUploadManager(
                this.state.fakeUploadInProgressFileForSelectedFiles,
                this.state.fileWithMetadataList,
                this.props,
                this.logger,
                isAudio ? [FromTypes.AUDIO] : [ConvertFromVideo.TYPE],
                isAudio ? ToTypes.AUDIO : ToTypes.VIDEO,
                () => { this.initialized = true; },
                () => { },
            );
            await fileUploadManager.fileSelected(event);

            if (this.state.fileWithMetadataList.length === 0) {
                this.setState({ isLoading: false, fileSelected: false } as Partial<S> as any);
                return;
            }
            const previewOptions: Record<string, any> = isAudio ? {
                process: 'COMPRESS',
                toMedia: 'AUDIO',
                toExtension: 'MP3',
                audioCodec: 'MP3',
                compression: 'CUSTOM_BITRATE',
                customBitrate: '128::kbps',
                audioChannelSelection: 'AUDIO_CHANNEL',
                audioChannel: 'ORIGINAL',
                audioSampleRateSelection: 'AUDIO_SAMPLE_RATE',
                audioSampleRate: 'ORIGINAL',
            } : {
                process: 'COMPRESS',
                toMedia: 'VIDEO',
                toExtension: 'MP4',
                videoCodec: 'H264',
                audioCodec: 'AAC',
                videoCompression: 'VIDEO_CONSTANT_QUALITY',
                videoCrf: 27,
                videoResolution: 'VIDEO_RESOLUTION_PRESET',
                videoPreset: '480p',
            };
            const fileUpload = this.convertNoneMergeUploadInProgressFilesToFileUploads(previewOptions)[0];

            this.processFile(fileUpload, () => {
                this.setState({ isLoading: false } as Partial<S> as any);
                this.forceUpdate();
            }, true);
            this.forceUpdate();
        } catch (error) {
            console.error(error);
            this.setState({ isLoading: false, fileSelected: false } as Partial<S> as any);
            this.forceUpdate();
        }
    }

    protected updateVideoSource(fileUpload: FileUpload) {
        if (!this.fileObjectURL) {
            const a = fileUpload.metadata.processorMetadataList[0];
            if (fileUpload.result) {
                this.fileUrl = ProcessorMetadataUtils.getDownloadEndpoint(a) +
                    '/range/' + fileUpload.result.outputFiles[0] +
                    `?content_type=${getFileMimeTypeFromFilename(fileUpload.result.outputFiles[0])}`;
            }
            const file = isArray(fileUpload.file) ? fileUpload.file[0] : fileUpload.file;
            if (file instanceof Blob && includes(PLAYABLE_FILE_MIMI_TYPES, file.type)) {
                if (!this.fileUrl) {
                    this.fileUrl = ProcessorMetadataUtils.getUploadedFileDownloadEndpoint(a) +
                        '/range/' + a.fileMetadata.safeName + `?content_type=${getFileMimeTypeFromFilename(a.fileMetadata.safeName)}`;
                }
                const URL = window.URL || window.webkitURL;
                this.fileObjectURL = URL.createObjectURL(file);
            }
            this.forceUpdate();
        }
    }

    convertNoneMergeUploadInProgressFilesToFileUploads(options): FileUpload[] {
        return this.state.fileWithMetadataList.map(fmd => {
            return this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd, options);
        });
    }

    convertNoneMergeUploadInProgressFilesToFileUpload(fmd: UploadInProgressFile<SupportedFileTypes>, extraOptions?: Record<string, any>): FileUpload {
        const options: Record<string, any> = { ...extraOptions };

        const isAudio = this.pageData.fromType.indexOf(FromTypes.AUDIO) > -1;

        const fileUpload = FileUploadUtils.toFileUpload(
            fmd,
            this.websocketService.getId(),
            ProcessTypes.CONVERT,
            isAudio ? [FromTypes.AUDIO] : [ConvertFromVideo.TYPE],
            isAudio ? ToTypes.AUDIO : ToTypes.VIDEO,
            options,
            null,
            fmd.uniqueProcessType,
            this.props.pageData,
        );

        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
        return fileUpload;
    }

    protected async processFile(fileUpload: FileUpload, postProcessFn: () => void, skipIfPlayable = false) {
        if (skipIfPlayable && fileUpload) {
            if (!isArray(fileUpload.file)) {
                const file: any = fileUpload.file;
                if (includes(PLAYABLE_FILE_MIMI_TYPES, file.type)) {
                    this.sourceFileUploadArray.splice(0, this.sourceFileUploadArray.length, fileUpload);
                    this.updateVideoSource(fileUpload);
                    postProcessFn();
                    return;
                }
            }
        }
        try {
            const instance = await this.spinnerService.show(`Submitted files. Submitting file ${fileUpload.name} to process...`);
            this.sourceFileUploadArray.splice(0, this.sourceFileUploadArray.length, fileUpload);
            this.fileManagerService.addFileUpload(fileUpload);
            this.fileProcessService.processFile(fileUpload).subscribe({
                next: (resp) => {
                },
                error: (err) => {
                    this.logger.error({ message: 'AV process failed.', error: err, data: FileUpload.toLoggableObject(fileUpload) });
                    this.spinnerService.hide(instance);
                    this.fileManagerService.removeFile(fileUpload);
                    if (ConnectivityUtils.isConnectivityError(err)) {
                        this.uiContext.showErrorDialog({
                            title: 'Connection Issue',
                            text: 'Could not reach the processing server. Please try again.',
                            confirmButtonText: 'Retry',
                            successCallback: () => {
                                this.fileManagerService.addFileUpload(fileUpload);
                                this.fileProcessService.processFile(fileUpload).subscribe({
                                    next: () => {
                                        this.fileManagerService.retrackFileUpload(fileUpload);
                                    },
                                    error: (retryErr) => {
                                        this.fileManagerService.removeFile(fileUpload);
                                        this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file.` });
                                    },
                                    complete: () => postProcessFn(),
                                });
                            },
                            showCancelButton: true,
                            cancelButtonText: 'Cancel',
                        });
                    } else {
                        this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                    }
                },
                complete: () => {
                    this.spinnerService.hide(instance);
                    postProcessFn();
                }
            });
            this.spinnerService.updateMessage(instance, `Successfully submitted ${fileUpload.name} for processing...`);
        } catch (e) {
            // suppress
        }
    }

    /** The xconfig tree a trim / cut job reads its per-output defaults from (av-job-options.ts). */
    protected loadAvJobTree(isAudio: boolean): Promise<unknown> {
        const slug = isAudio ? AV_JOB_TREE.audio : AV_JOB_TREE.video;
        let pending = avJobTrees.get(slug);
        if (!pending) {
            pending = new Promise((resolve, reject) => {
                this.pageDataProvider.loadConvertOptions(slug).subscribe({ next: resolve, error: reject });
            });
            pending.catch(() => avJobTrees.delete(slug));
            avJobTrees.set(slug, pending);
        }
        return pending;
    }

    /** Resolves the job tree, or tells the user and returns null when it cannot be loaded. */
    protected async avJobTreeOrNull(isAudio: boolean): Promise<unknown | null> {
        try {
            return await this.loadAvJobTree(isAudio);
        } catch (e) {
            this.logger.error({ message: 'Failed to load the trim/cut option tree', error: e as Error });
            this.uiContext.showErrorDialog({
                title: 'Error', text: 'Could not prepare this file. Please check your connection and try again.'
            });
            return null;
        }
    }

    updateToVideoType(selectedItem: any) {
        this.selectedToType = selectedItem.item;
    }

    protected updateSupportedAVTypes(pageData: PageData) {
        const types = pageData.fromType.indexOf(FromTypes.AUDIO) > -1 ? AV_OUTPUT_TYPES.audio : AV_OUTPUT_TYPES.video;
        this.supportedAVTypes = types.map(t => ({ ...t }));
    }

    protected updatePageData(value: PageData) {
        this.pageData = value;
        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.uploadedFilesService.initializeForCompatibility(value.fromType, value.accept);
        this.uploadedFilesService.notyfyAll();
    }

    startNew() {
        window.location.reload();
    }
}
